import {
  AreaCategory,
  IDisplayableUserProfile,
  ProcessTypes,
  UserProfile,
  UserWorkoutSession,
  WorkoutSettings,
  staticMachineAreaZones,
} from 'fitbeat.gym-core';
import {
  CardioOrResistance,
  EnabledFeatures,
  GymTypes,
  IDay,
  IEnabledFeatures,
  ILiveStreamOptions,
} from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, Platform, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import { getInset } from 'react-native-safe-area-view';
import { connect } from 'react-redux';
import { enabledFeaturesSelector } from '../../auth/auth-selectors';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import Theme from '../../theme';
import { FormError } from '../../ui';
import { i18n } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import {
  changeBooking,
  customiseBookingSettingsRequest,
  getNextBookingStep,
  getPreviousBookingStep,
  getRecommendedSettings,
  makeBooking,
  workoutTypeChanged,
} from '../actions';
import {
  BookingFlowFooter,
  WhatYouNeedForWokoutText,
  WorkoutFormat,
  WorkoutSettingsSummary,
  WorkoutTypeWithBodyGroupText,
} from '../components';
import { BookingSummary } from '../components/BookingSummary';

import { build } from '../../config';
import { IAppStore } from '../../HomeWorkouts';
import { GymResolver } from '../../services';
import { StatusBarComponent } from '../../ui/components/StatusBar';
import { createWaitlistServiceClient } from '../../utils/apiClientFactory';
import { TypeOfWorkout } from '../components/TypeOfWorkout';
import { LivestreamOptionsSummary } from './LivestreamOptionsSummary';
import SessionTimings from '../components/SessionTimings';
import { recommendedSettingsSelector } from '../selectors';

const { isElite } = build.variant;

const bottomInset = Platform.OS !== 'web' ? getInset('bottom', false) : 0;
export interface IProps {
  areaCategory: AreaCategory;
  day: IDay;
  userProfile: UserProfile;
  enabledFeatures: IEnabledFeatures;
  workoutSession: UserWorkoutSession;
  workoutSessionService: any;
  isSoloWorkout: boolean;
  recommendedSettingsLoading: boolean;
  recommendedSettings: WorkoutSettings;
  studioPartner: IDisplayableUserProfile | null;
  makeBookingError: string;
  livestreamOptions?: ILiveStreamOptions;
  livestreamWorkout: boolean;
  t(key: string, options?: any): string;
  setSoloWorkout(isSoloWorkout: boolean): void;
  setPendingSlot(pendingSlot): void;
  getRecommendedSettings(workoutSessionService: any, userProfile: UserProfile, day: IDay): void;
}

function WorkoutSummary({
  t,
  areaCategory,
  day,
  userProfile,
  enabledFeatures,
  workoutSession,
  isSoloWorkout,
  pendingSlot,
  currentSelectedGymName,
  workoutSessionService,
  getNextBookingStep,
  makeBooking,
  changeBooking,
  makeBookingLoading,
  makeBookingError,
  getPreviousBookingStep,
  recommendedSettingsLoading,
  recommendedSettings,
  customiseBookingSettingsRequest,
  getRecommendedSettings,
  studioPartner,
  closeModal,
  workoutType,
  workoutTypeSelected,
  livestreamOptions,
  livestreamWorkout,
}: IProps) {
  const gym = GymResolver.getByName(currentSelectedGymName);
  const showGymEquipment = gym.type === GymTypes.Home && EnabledFeatures.canSelectEquipment(enabledFeatures);
  const isHomeBooking = gym.type === GymTypes.Home;

  if (makeBookingLoading || recommendedSettingsLoading) {
    return (
      <ActivityIndicator
        size='small'
        animating
        color={Theme.PRIMARY}
        style={styles.container}
      />
    );
  }

  const personaliseWorkout = async () => {
    getNextBookingStep(userProfile, ProcessTypes.CustomiseWorkout);
    customiseBookingSettingsRequest();
  };

  const showWorkoutTypeWarning = (cardioOrResistance) => {
    getNextBookingStep(userProfile, ProcessTypes.TypeOfWorkoutWarning);
    workoutTypeSelected(cardioOrResistance);
  };

  const makeOrChangeBooking = () => {
    if (workoutSession) {
      changeBooking(workoutSessionService, userProfile, day);
    } else {
      makeBooking(workoutSessionService, userProfile, day);
    }
  };

  const shouldOnlyShowSessionTimings = !isHomeBooking && staticMachineAreaZones.includes(areaCategory);

  return (
    <View style={styles.container}>
      <StatusBarComponent barStyle='light-content' />
      <View style={styles.container}>
        <ScrollView>
          <BookingSummary
            t={t}
            gym={gym}
            pendingSlot={pendingSlot}
            studioPartner={studioPartner}
            isSoloWorkout={isSoloWorkout}
            workoutSettings={recommendedSettings}
            closeModal={closeModal}
            areaCategory={isHomeBooking ? 'home' : areaCategory}
          />
          {!shouldOnlyShowSessionTimings && (

            <View style={styles.tile}>
              <Text style={[Theme.textStyle.heading2, { marginBottom: defaultScale(18) }]}>
                {t('typeOfWorkout')}
              </Text>
              <TypeOfWorkout
                workoutSettings={recommendedSettings}
                workoutTypeSelected={showWorkoutTypeWarning}
                areaCategory={areaCategory}
              />
            </View>
          )}
          {livestreamWorkout
            && (
            <View style={styles.tile}>
              <LivestreamOptionsSummary
                livestreamOptions={livestreamOptions}
                startTimeOfFirstExercise={pendingSlot.startTime}
                t={t}
              />
            </View>
            )}
          {!shouldOnlyShowSessionTimings && (
            <View style={styles.tile}>
              <Text style={[Theme.textStyle.heading2]}>
                {t('yourWorkoutSentenceCase')}
              </Text>
              {recommendedSettings
                ? (
                  <WorkoutSettingsSummary
                    t={t}
                    gym={gym}
                    gender={userProfile.gender}
                    workoutSettings={recommendedSettings}
                    personaliseWorkout={personaliseWorkout}
                    showGymEquipment={showGymEquipment}
                    showCusomiseWorkoutSettings
                  />
                )
                : (
                  <>
                    <FormError error={new Error(t('recommendedSettingsFailed'))} />
                    <Button
                      fontColor={Theme.WHITE}
                      title={t('retry')}
                      onPress={() => getRecommendedSettings(workoutSessionService, userProfile, day)}
                    />
                  </>
                )}
            </View>
          )}
          {!isHomeBooking && (
            <SessionTimings
              t={t}
              areaCategory={areaCategory}
              startTime={pendingSlot.startTime}
              endTime={pendingSlot.endTime}
            />
          )}
          {!shouldOnlyShowSessionTimings && (
            <View style={styles.tile}>
              <WorkoutTypeWithBodyGroupText
                t={t}
                gym={gym}
                workoutSettings={recommendedSettings}
              />
            </View>
          )}
          {isHomeBooking && !isElite && (
          <View style={styles.tile}>
            <WhatYouNeedForWokoutText
              t={t}
              equipment={recommendedSettings?.equipment}
            />
          </View>
          )}
          {isHomeBooking && (
          <View style={styles.tile}>
            <WorkoutFormat t={t} formatType={workoutType} />
          </View>
          )}
        </ScrollView>
      </View>
      <BookingFlowFooter>
        {makeBookingError && <FormError error={new Error(makeBookingError)} />}
        <View style={styles.footer}>
          <Button
            fontColor={Theme.PRIMARY}
            size={ButtonSize.MEDIUM}
            buttonType={ButtonTypes.OUTLINE}
            title={i18n.t('bookings:back')}
            onPress={getPreviousBookingStep}
            containerStyle={styles.backContainer}
          />
          <Button
            disabled={!recommendedSettings}
            fontColor={Theme.WHITE}
            size={ButtonSize.MEDIUM}
            title={i18n.t('bookings:continueBooking')}
            onPress={makeOrChangeBooking}
            containerStyle={styles.nextContainer}
          />
        </View>
      </BookingFlowFooter>
    </View>
  );
}

const mapStateToProps = (state: IAppStore): Partial<IProps> => ({
  isSoloWorkout: state.bookings.bookingWizard.isSoloWorkout,
  currentSelectedGymName: state.bookings.bookingWizard.currentSelectedGymName,
  pendingSlot: state.bookings.bookingWizard.pendingSlot,
  makeBookingLoading: state.bookings.bookingWizard.makeBookingLoading,
  makeBookingError: state.bookings.bookingWizard.makeBookingError,
  recommendedSettingsLoading: state.bookings.bookingWizard.recommendedSettingsLoading,
  recommendedSettings: recommendedSettingsSelector(state),
  workoutSession: state.bookings.bookingWizard.workoutSession,
  studioPartner: state.bookings.bookingWizard.studioPartner,
  workoutType: state.bookings.bookingWizard.workoutType,
  enabledFeatures: enabledFeaturesSelector(state),
  livestreamOptions: state.bookings.bookingWizard.livestreamOptions,
  livestreamWorkout: state.bookings.bookingWizard.livestreamToggleValue,
  areaCategory: state.bookings.bookingWizard.areaCategory,
});

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
  const waitingListServiceClient = createWaitlistServiceClient();

  return {
    getNextBookingStep: (userProfile, processType) => dispatch(getNextBookingStep(userProfile, processType, undefined)),
    getPreviousBookingStep: () => dispatch(getPreviousBookingStep()),
    makeBooking: (
      workoutSessionService,
      userProfile,
      day,
    ) => dispatch(makeBooking(workoutSessionService, waitingListServiceClient, userProfile, day)),
    changeBooking: (
      workoutSessionService,
      userProfile,
      day,
    ) => dispatch(changeBooking(workoutSessionService, userProfile, day)),
    getRecommendedSettings: (
      workoutSessionService: any,
      userProfile: UserProfile,
      day: IDay,
    ) => dispatch(getRecommendedSettings(workoutSessionService, userProfile, day)),
    customiseBookingSettingsRequest: () => dispatch(customiseBookingSettingsRequest()),
    workoutTypeSelected: (cardioOrResistance: CardioOrResistance) => dispatch(workoutTypeChanged(cardioOrResistance)),
  };
};

export const WorkoutSummaryStep = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common'], { wait: true })(WorkoutSummary),
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.GREY_LIGHT,
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: defaultScale(15),
    paddingBottom: defaultScale(15 + bottomInset),
  },
  backContainer: {
    flex: 0.3,
  },
  nextContainer: {
    flex: 0.7,
    marginLeft: 5,
  },
  settingContainer: {
    paddingBottom: 10,
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: Theme.GREY_LIGHT,
  },
  tile: {
    backgroundColor: Theme.WHITE,
    marginTop: defaultScale(10),
    paddingHorizontal: defaultScale(18),
    paddingVertical: defaultScale(22),
  },
});
